import { HALLIDAY_DOMAIN, HALLIDAY_PAYMENTS_API_URL } from "@shared/constants";
import { TransactionReceipt, Address } from "@shared/types";
import { isMobile } from "@shared/utils/browserUtils";

import {
    AppMode,
    Message,
    MessageResponse,
    MessageType,
    PaymentCategoryOrder,
    PaymentsWidgetSDKParams,
    SDKOverrides,
    SendTransaction,
    SignMessage,
    SignTypedData,
} from "./types";
import {
    _openPaymentsWidget,
    _openProviderWidget,
    _waitForPaymentStatus,
    logError,
    createEmbedSkeleton,
    normalizePaymentCategoryOrder,
} from "./utils";
import { isPreloaded, isInitialized, _showWidget, getOverrides, getInitConfig } from "./preload";
import { StatusCallback } from "./types";

const PRELOAD_WAIT_TIMEOUT_MS = 30000;

/**
 * Creates message handlers for widget communication.
 * Shared between preloaded and popup paths to avoid code duplication.
 */
const _createMessageHandlers = (config: {
    windowOrigin: string;
    widgetWindow: Window;
    providerWindowRef: { current: Window | null };
    cleanMessageHandlers: () => void;
    hasOwner: boolean;
    hasTxHandler: boolean;
    signMessage?: SignMessage;
    signTypedData?: SignTypedData;
    sendTransaction?: SendTransaction;
    statusCallback?: StatusCallback;
    apiKey: string;
    apiBaseUrl: string;
}): ((event: MessageEvent<Message>) => void)[] => {
    const {
        windowOrigin,
        widgetWindow,
        providerWindowRef,
        cleanMessageHandlers,
        hasOwner,
        hasTxHandler,
        signMessage,
        signTypedData,
        sendTransaction,
        statusCallback,
        apiKey,
        apiBaseUrl,
    } = config;

    const handlers: ((event: MessageEvent<Message>) => void)[] = [];

    // Close handler
    handlers.push((event: MessageEvent<Message>) => {
        if (event.origin !== windowOrigin || event.source !== widgetWindow) return;
        const { type } = event.data || {};
        if (type === MessageType.EVENT_WINDOW_CLOSE) {
            cleanMessageHandlers();
        }
    });

    // Signature handlers
    if (hasOwner) {
        handlers.push(async (event: MessageEvent<Message>) => {
            if (event.origin !== windowOrigin || event.source !== widgetWindow) return;
            const { type, payload } = event.data || {};

            if (type === MessageType.ACTION_SIGN_MESSAGE) {
                const { messageId, message, ownerAddress: msgOwnerAddress } = payload;
                try {
                    const signature =
                        typeof signMessage === "function"
                            ? await signMessage({
                                  message,
                                  ...(msgOwnerAddress !== undefined && { ownerAddress: msgOwnerAddress }),
                              })
                            : (() => {
                                  throw new Error("No signMessage function provided");
                              })();
                    widgetWindow.postMessage(
                        { type, payload: { messageId, signature } } as MessageResponse,
                        windowOrigin,
                    );
                } catch (error) {
                    widgetWindow.postMessage(
                        {
                            type,
                            payload: { messageId, error: error instanceof Error ? error.message : error },
                        } as MessageResponse,
                        windowOrigin,
                    );
                }
            } else if (type === MessageType.ACTION_SIGN_TYPED_DATA) {
                const { messageId, typedData, ownerAddress: tdOwnerAddress } = payload;
                try {
                    const signature =
                        typeof signTypedData === "function"
                            ? await signTypedData({
                                  typedData,
                                  ...(tdOwnerAddress !== undefined && { ownerAddress: tdOwnerAddress }),
                              })
                            : (() => {
                                  throw new Error("No signTypedData function provided");
                              })();
                    widgetWindow.postMessage(
                        { type, payload: { messageId, signature } } as MessageResponse,
                        windowOrigin,
                    );
                } catch (error) {
                    widgetWindow.postMessage(
                        {
                            type,
                            payload: { messageId, error: error instanceof Error ? error.message : error },
                        } as MessageResponse,
                        windowOrigin,
                    );
                }
            }
        });
    }

    // Transaction handler
    if (hasTxHandler) {
        handlers.push(async (event: MessageEvent<Message>) => {
            if (event.origin !== windowOrigin || event.source !== widgetWindow) return;
            const { type, payload } = event.data || {};

            if (type === MessageType.ACTION_TRANSACTION) {
                const { messageId, chainConfig, transaction } = payload;
                try {
                    const txReceipt =
                        typeof sendTransaction === "function"
                            ? await sendTransaction(transaction, chainConfig)
                            : (() => {
                                  throw new Error("No sendTransaction function provided");
                              })();
                    widgetWindow.postMessage(
                        { type, payload: { messageId, txReceipt } } as MessageResponse,
                        windowOrigin,
                    );
                } catch (error) {
                    widgetWindow.postMessage(
                        {
                            type,
                            payload: { messageId, error: error instanceof Error ? error.message : error },
                        } as MessageResponse,
                        windowOrigin,
                    );
                }
            }
        });
    }

    // Status callback handler
    if (statusCallback) {
        handlers.push((event: MessageEvent<Message>) => {
            if (event.origin !== windowOrigin || event.source !== widgetWindow) return;
            const { type, payload } = event.data || {};
            if (type === MessageType.EVENT_ORDER_STATUS) {
                statusCallback({ type, payload });
            }
        });
    }

    // Provider widget handler
    handlers.push(async (event: MessageEvent<Message>) => {
        if (event.origin !== windowOrigin || event.source !== widgetWindow) return;
        const { type, payload } = event.data || {};

        if (type === MessageType.ACTION_PROVIDER_WIDGET) {
            const { url, redirectUrl, providerWidgetMode, workflowId } = payload || {};
            if (!url || !redirectUrl || !providerWidgetMode || !workflowId) return;

            let tempWindow: Window | null = null;
            if (providerWidgetMode === "REDIRECT") {
                (widgetWindow as Window & { location: string }).location = url;
                tempWindow = widgetWindow;
            } else if (providerWidgetMode === "POPUP") {
                providerWindowRef.current = _openProviderWidget(url);
                tempWindow = providerWindowRef.current;
            }

            if (tempWindow) {
                await _waitForPaymentStatus({
                    apiKey,
                    baseURL: apiBaseUrl,
                    workflowId,
                    window: tempWindow,
                });

                if (!tempWindow.closed) {
                    if (providerWidgetMode === "REDIRECT") {
                        (widgetWindow as Window & { location: string }).location = redirectUrl;
                    } else if (providerWidgetMode === "POPUP") {
                        providerWindowRef.current?.close();
                        providerWindowRef.current = null;
                    }
                }
            }
        }
    });

    return handlers;
};

const _windowManager = (() => {
    let widgetWindow: Window | null = null;
    let providerWindow: Window | null = null;
    const providerWindowRef = {
        get current() {
            return providerWindow;
        },
        set current(w) {
            providerWindow = w;
        },
    };
    let handlers: ((event: MessageEvent<Message>) => void)[] = [];

    const cleanMessageHandlers = () => {
        handlers.forEach((handler) => {
            window.removeEventListener("message", handler);
        });
        handlers = [];
    };

    const registerMessageHandlers = (newHandlers: ((event: MessageEvent<Message>) => void)[]) => {
        newHandlers.forEach((handler) => {
            window.addEventListener("message", handler);
        });
        handlers = newHandlers;
    };

    const openWidget = async (params: PaymentsWidgetSDKParams, ...args: any[]) => {
        const overridesArg = args[999] || args[0] || {};

        const openNormalPath = async () => {
            // ==========================================
            // NORMAL PATH: Original flow (unchanged)
            // No preloading - used when initHallidayPayments was NOT called
            // ==========================================
            let validatedParams: PaymentsWidgetSDKParams;
            try {
                validatedParams = PaymentsWidgetSDKParams.parse(params) as PaymentsWidgetSDKParams;
            } catch (error) {
                logError(error);
                throw error;
            }
            let validatedOverrides: SDKOverrides;
            try {
                validatedOverrides = SDKOverrides.parse(overridesArg);
            } catch (error) {
                logError(error);
                throw error;
            }

            const {
                dangerouslyOverrideHallidayDomainName,
                dangerouslyOverrideApiBaseUrl,
                dangerouslyOverrideIPAddress,
                dangerouslyOverrideFeatureFlags,
            } = validatedOverrides;

            // Extract the required parameters
            const { owner, statusCallback, funder, windowType: normalWindowType } = validatedParams;
            // Remove trailing slash for consistent origin comparison
            const windowOrigin = (dangerouslyOverrideHallidayDomainName || HALLIDAY_DOMAIN).replace(/\/$/, "");
            const signMessage: SignMessage | undefined = owner?.signMessage;
            const signTypedData: SignTypedData | undefined = owner?.signTypedData;
            const sendTransaction: SendTransaction | undefined = funder?.sendTransaction || owner?.sendTransaction;
            const ownerAddress: Address | undefined = owner?.address;
            const funderAddress: Address | undefined = funder?.address;

            const hasOwner = Address.safeParse(ownerAddress).success && typeof signTypedData === "function";
            const hasTxHandler = Address.safeParse(funderAddress).success && typeof sendTransaction === "function";
            let hostOrigin: string | null = null;
            let appMode: AppMode = "FULL";
            try {
                hostOrigin = window.origin;
                if (normalWindowType === "MODAL") {
                    if (isMobile()) {
                        appMode = "OVERLAY";
                    } else {
                        appMode = "MODAL";
                    }
                } else if (normalWindowType === "EMBED") {
                    // Use EMBED appMode for content-sized widget with dynamic resizing
                    appMode = "EMBED";
                }
            } catch (error) {
                // It is ok if access to window.origin is denied
            }

            // Clean up the previous commerce window
            cleanMessageHandlers();
            const messageHandlers: ((event: MessageEvent<Message>) => void)[] = [
                // handler for window close event
                (event: MessageEvent<Message>) => {
                    // Verify message origin
                    if (event.origin !== windowOrigin || event.source !== widgetWindow) {
                        return;
                    }

                    const { type } = event.data || {};
                    if (type === MessageType.EVENT_WINDOW_CLOSE) {
                        cleanMessageHandlers();
                    }
                },
            ];

            // Register message handler if owner is providered
            if (hasOwner) {
                const signatureMessageHandler = async (event: MessageEvent<Message>) => {
                    // Verify message origin
                    if (event.origin !== windowOrigin || event.source !== widgetWindow) {
                        return;
                    }

                    const { type, payload } = event.data || {};
                    if (type === MessageType.ACTION_SIGN_MESSAGE) {
                        const { messageId, message, ownerAddress } = payload;
                        try {
                            let signature: string;
                            if (typeof signMessage === "function") {
                                signature = await signMessage({
                                    message,
                                    ...(ownerAddress !== undefined && { ownerAddress }),
                                });
                            } else {
                                throw new Error("No signMessage function provided");
                            }

                            widgetWindow?.postMessage(
                                { type, payload: { messageId, signature } } as MessageResponse,
                                windowOrigin,
                            );
                        } catch (error) {
                            console.error("Error signing message", error);
                            widgetWindow?.postMessage(
                                {
                                    type,
                                    payload: {
                                        messageId,
                                        error: error instanceof Error ? error.message : error,
                                    },
                                } as MessageResponse,
                                windowOrigin,
                            );
                        }
                    } else if (type === MessageType.ACTION_SIGN_TYPED_DATA) {
                        const { messageId, typedData, ownerAddress } = payload;
                        try {
                            let signature: string;
                            if (typeof signTypedData === "function") {
                                signature = await signTypedData({
                                    typedData,
                                    ...(ownerAddress !== undefined && { ownerAddress }),
                                });
                            } else {
                                throw new Error("No signTypedData function provided");
                            }

                            widgetWindow?.postMessage(
                                { type, payload: { messageId, signature } } as MessageResponse,
                                windowOrigin,
                            );
                        } catch (error) {
                            console.error("Error signing typed data", error);
                            widgetWindow?.postMessage(
                                {
                                    type,
                                    payload: {
                                        messageId,
                                        error: error instanceof Error ? error.message : error,
                                    },
                                } as MessageResponse,
                                windowOrigin,
                            );
                        }
                    }
                };

                messageHandlers.push(signatureMessageHandler);
            }

            if (hasTxHandler) {
                const transactionMessageHandler = async (event: MessageEvent<Message>) => {
                    // Verify message origin
                    if (event.origin !== windowOrigin || event.source !== widgetWindow) {
                        return;
                    }

                    const { type, payload } = event.data || {};
                    if (type === MessageType.ACTION_TRANSACTION) {
                        const { messageId, chainConfig, transaction } = payload;
                        try {
                            let txReceipt: TransactionReceipt;
                            if (typeof sendTransaction === "function") {
                                txReceipt = await sendTransaction(transaction, chainConfig);
                            } else {
                                throw new Error("No sendTransaction function provided");
                            }
                            // Respond with the transaction receipt
                            widgetWindow?.postMessage(
                                { type, payload: { messageId, txReceipt } } as MessageResponse,
                                windowOrigin,
                            );
                        } catch (error) {
                            console.error("Error handling transaction", error);
                            widgetWindow?.postMessage(
                                {
                                    type,
                                    payload: {
                                        messageId,
                                        error: error instanceof Error ? error.message : error,
                                    },
                                } as MessageResponse,
                                windowOrigin,
                            );
                        }
                    }
                };

                messageHandlers.push(transactionMessageHandler);
            }

            if (statusCallback) {
                const statusMessageHandler = (event: MessageEvent<Message>) => {
                    // Verify message origin
                    if (event.origin !== windowOrigin || event.source !== widgetWindow) {
                        return;
                    }

                    const { type, payload } = event.data || {};
                    if (type === MessageType.EVENT_ORDER_STATUS) {
                        statusCallback({ type, payload });
                    }
                };

                messageHandlers.push(statusMessageHandler);
            }

            const providerWidgetActionHandler = async (event: MessageEvent<Message>) => {
                // Verify message origin
                if (event.origin !== windowOrigin || event.source !== widgetWindow) {
                    return;
                }

                const { type, payload } = event.data || {};
                if (type === MessageType.ACTION_PROVIDER_WIDGET && widgetWindow) {
                    const { url, redirectUrl, providerWidgetMode, workflowId } = payload || {};

                    // Validate the payload
                    if (!url || !redirectUrl || !providerWidgetMode || !workflowId) {
                        throw new Error("Invalid provider widget payload");
                    }

                    let tempWindow: Window | null = null;
                    // Open the provider widget
                    if (providerWidgetMode === "REDIRECT") {
                        widgetWindow!.location = url;
                        tempWindow = widgetWindow;
                    } else if (providerWidgetMode === "POPUP") {
                        providerWindow = _openProviderWidget(url);
                        tempWindow = providerWindow;
                    } else {
                        throw new Error("Invalid provider widget mode");
                    }

                    // Fetch the status of the payment
                    await _waitForPaymentStatus({
                        apiKey: validatedParams.apiKey,
                        baseURL: dangerouslyOverrideApiBaseUrl || HALLIDAY_PAYMENTS_API_URL,
                        workflowId,
                        window: tempWindow!,
                    });

                    if (tempWindow?.closed) {
                        // The window was closed
                        return;
                    }

                    if (providerWidgetMode === "REDIRECT") {
                        widgetWindow!.location = redirectUrl;
                    } else if (providerWidgetMode === "POPUP") {
                        providerWindow?.close();
                        providerWindow = null;
                    }
                }
            };
            messageHandlers.push(providerWidgetActionHandler);

            // Build widget params
            const widgetParams = {
                ...validatedParams,
                sandbox: validatedParams.sandbox || false,
                hasOwner,
                hasTxHandler,
                windowOrigin,
                apiBaseUrl: dangerouslyOverrideApiBaseUrl,
                hostOrigin,
                ownerAddress,
                funderAddress,
                ipAddress: dangerouslyOverrideIPAddress,
                featureFlags: dangerouslyOverrideFeatureFlags,
                appMode,
                fontName: validatedParams.fontName,
                paymentCategoryOrder: normalizePaymentCategoryOrder(
                    validatedParams.paymentCategoryOrder as PaymentCategoryOrder | undefined,
                ),
                headerTitle: validatedParams.headerTitle,
                onramps: validatedParams.onramps,
            };

            // Open the widget (fallback when not initialized via init())
            // Note: Skeleton was already shown at the start of openWidget
            widgetWindow = _openPaymentsWidget(widgetParams);

            // Register message handlers
            registerMessageHandlers(messageHandlers);
        };

        // ==========================================
        // EMBED SKELETON: Show immediately for best UX
        // ==========================================
        // Show skeleton RIGHT NOW before any validation or async work
        // This ensures instant visual feedback for EMBED mode
        const windowType = params.windowType;
        const targetElementId = params.targetElementId;

        // Skeleton options from params
        // Use skeletonBackgroundColor if provided, otherwise fall back to customStyles.backgroundColor
        const showSkeleton = params.showSkeleton ?? true;
        const skeletonBackgroundColor = params.skeletonBackgroundColor ?? params.customStyles?.backgroundColor;

        // Show skeleton immediately for EMBED mode
        if (windowType === "EMBED" && targetElementId && showSkeleton) {
            const targetElement = document.getElementById(targetElementId);
            if (targetElement) {
                // Don't show skeleton if widget is already preloaded and ready
                if (!isPreloaded()) {
                    createEmbedSkeleton(targetElement, skeletonBackgroundColor);
                }
            }
        }

        // ==========================================
        // PRELOADED PATH: Show preloaded widget
        // ==========================================
        // If initHallidayPayments was called and widget is ready, show it with merged params
        if (isPreloaded()) {
            const initConfig = getInitConfig();
            const storedOverrides = getOverrides();
            // Remove trailing slash for consistent origin comparison
            const windowOrigin = (storedOverrides?.dangerouslyOverrideHallidayDomainName || HALLIDAY_DOMAIN).replace(
                /\/$/,
                "",
            );

            // Extract function params from open call
            const { owner, funder, statusCallback } = params;
            const signMessage: SignMessage | undefined = owner?.signMessage;
            const signTypedData: SignTypedData | undefined = owner?.signTypedData;
            const sendTransaction: SendTransaction | undefined = funder?.sendTransaction || owner?.sendTransaction;
            const ownerAddress: Address | undefined = owner?.address;
            const funderAddress: Address | undefined = funder?.address;

            const hasOwner = Address.safeParse(ownerAddress).success && typeof signTypedData === "function";
            const hasTxHandler = Address.safeParse(funderAddress).success && typeof sendTransaction === "function";

            // windowType and targetElementId come from openParams
            const windowType = params.windowType || "MODAL";
            const targetElementId = params.targetElementId;

            // Determine app mode based on windowType
            let hostOrigin: string | null = null;
            let appMode: AppMode = "FULL";
            try {
                hostOrigin = window.origin;
                if (windowType === "MODAL") {
                    appMode = isMobile() ? "OVERLAY" : "MODAL";
                } else if (windowType === "EMBED") {
                    appMode = "EMBED";
                }
            } catch {
                // OK if window.origin access denied
            }

            // Build params to send to the widget
            // All business params come from openHallidayPayments (init only has apiKey/sandbox)
            const mergedParams = {
                apiKey: params.apiKey || initConfig?.apiKey || "",
                sandbox: params.sandbox ?? initConfig?.sandbox ?? false,
                widgetVersion: params.widgetVersion,
                destinationAddress: params.destinationAddress,
                onramps: params.onramps,
                outputs: params.outputs ?? [],
                inputs: params.inputs,
                customStyles: params.customStyles,
                redirects: params.redirects,
                features: params.features,
                paymentCategoryOrder: normalizePaymentCategoryOrder(
                    params.paymentCategoryOrder as PaymentCategoryOrder | undefined,
                ),
                fontName: params.fontName,
                headerTitle: params.headerTitle,
                windowType,
                targetElementId,
                // SDK additions
                hasOwner,
                hasTxHandler,
                ownerAddress,
                funderAddress,
                hostOrigin,
                appMode,
                windowOrigin,
                apiBaseUrl:
                    overridesArg?.dangerouslyOverrideApiBaseUrl ?? storedOverrides?.dangerouslyOverrideApiBaseUrl,
                ipAddress: overridesArg?.dangerouslyOverrideIPAddress ?? storedOverrides?.dangerouslyOverrideIPAddress,
                featureFlags:
                    overridesArg?.dangerouslyOverrideFeatureFlags ?? storedOverrides?.dangerouslyOverrideFeatureFlags,
            };

            // Show the preloaded widget with merged params
            widgetWindow = _showWidget(mergedParams);

            if (widgetWindow) {
                // Set up message handlers using shared factory
                cleanMessageHandlers();
                const messageHandlers = _createMessageHandlers({
                    windowOrigin,
                    widgetWindow,
                    providerWindowRef,
                    cleanMessageHandlers,
                    hasOwner,
                    hasTxHandler,
                    ...(signMessage !== undefined && { signMessage }),
                    ...(signTypedData !== undefined && { signTypedData }),
                    ...(sendTransaction !== undefined && { sendTransaction }),
                    ...(statusCallback !== undefined && { statusCallback }),
                    apiKey: initConfig?.apiKey || params.apiKey,
                    apiBaseUrl: storedOverrides?.dangerouslyOverrideApiBaseUrl || HALLIDAY_PAYMENTS_API_URL,
                });
                registerMessageHandlers(messageHandlers);
                return;
            }

            // POPUP mode: _showWidget returns null, but we still have preloaded resources
            // Fall through to use _openPaymentsWidget with merged params
            if (windowType === "POPUP") {
                // Use _openPaymentsWidget which does window.open()
                // Resources are already cached from the preload iframe
                widgetWindow = _openPaymentsWidget({
                    ...mergedParams,
                    windowOrigin,
                });

                if (widgetWindow) {
                    // Set up message handlers using shared factory
                    cleanMessageHandlers();
                    const messageHandlers = _createMessageHandlers({
                        windowOrigin,
                        widgetWindow,
                        providerWindowRef,
                        cleanMessageHandlers,
                        hasOwner,
                        hasTxHandler,
                        ...(signMessage !== undefined && { signMessage }),
                        ...(signTypedData !== undefined && { signTypedData }),
                        ...(sendTransaction !== undefined && { sendTransaction }),
                        ...(statusCallback !== undefined && { statusCallback }),
                        apiKey: initConfig?.apiKey || params.apiKey,
                        apiBaseUrl: storedOverrides?.dangerouslyOverrideApiBaseUrl || HALLIDAY_PAYMENTS_API_URL,
                    });
                    registerMessageHandlers(messageHandlers);
                    return;
                }
            }
            // Fall through to normal open if _showWidget failed for other reasons
        }

        // ==========================================
        // INITIALIZED BUT NOT READY: Wait for preload to complete
        // ==========================================
        // If init was called but widget isn't ready yet, wait for it
        // Skeleton was already shown by showEmbedSkeleton() above
        if (isInitialized() && !isPreloaded()) {
            // Wait for preload to complete, then show widget
            return new Promise<void>((resolve) => {
                const startTime = Date.now();
                const checkReady = () => {
                    if (isPreloaded()) {
                        // Now ready - call openWidget again (will take the preloaded path)
                        openWidget(params, ...args).then(resolve);
                    } else if (Date.now() - startTime >= PRELOAD_WAIT_TIMEOUT_MS) {
                        // Timeout fallback: open normally if preload never completes
                        openNormalPath().then(resolve);
                    } else {
                        // Check again in 100ms
                        setTimeout(checkReady, 100);
                    }
                };
                checkReady();
            });
        }

        return openNormalPath();
    };

    return { openWidget };
})();

/**
 * Opens the Halliday Payments widget.
 *
 * This function initializes and opens the payment widget window in a popup or embedded mode.
 *
 * @param {PaymentsWidgetSDKParams} params The configurations for the payments widget, which includes:
 *
 * @param {!string} params.apiKey {string} Your API key for authorization.
 * @param {boolean=} params.sandbox {boolean} (optional) Whether the widget is in sandbox mode.
 * @param {string=} params.destinationAddress {string} (optional) The address of the destination of the widget.
 *
 * @param {Asset[]=} params.inputs {{@link Asset}[]} (optional) The input assets for the widget.
 * @param {Asset[]=} params.outputs {{@link Asset}[]} (optional) The output assets for the widget.
 * @param {string[]=} params.onramps {string[]} (optional) Array of onramp provider names to filter/display in the widget.
 * @param {RampName[]=} params.onramps {{@link RampName}[]} (optional) The onramps for the widget.
 * @param {RampName[]=} params.offramps {{@link RampName}[]} (optional) The offramps for the widget.
 *
 * @param {CustomStyles=} params.customStyles {{@link CustomStyles}} (optional) A list of custom styles to show in the widget.
 * @param {string=} params.targetElementId {string} (required if windowType is "EMBED") The ID of the DOM element where the widget should be embedded.
 * @param {("POPUP" | "EMBED")=} params.windowType {"POPUP" | "EMBED"} The desired display mode of the widget.
 *
 * @param {Role=} params.owner {{@link Role}} (optional) The owner that would have full access to payments workflow.
 * @param {Role=} params.funder {{@link Role}} (optional) The funder that initiates the funding.
 * @param {PaymentCategoryOrder=} params.paymentCategoryOrder {{@link PaymentCategoryOrder}} (optional) On the quote page, these are three displayed paymentcategories (empty or three elements).
 * @param {FontName=} params.fontName {{@link FontName}} (optional) The font name to use in the widget (haffer or wudoo-mono or inter). Defaults to haffer.
 * @param {HeaderTitle=} params.headerTitle {{@link HeaderTitle}} (optional) The header title to use in the widget (Buy or string). Defaults to Buy.
 *
 * @param {StatusCallback=} params.statusCallback {{@link StatusCallback}} (optional) Callback to receive status events.
 */
export async function openHallidayPayments(params: PaymentsWidgetSDKParams, ...args: any[]): Promise<void> {
    try {
        return _windowManager.openWidget(params, ...args);
    } catch (error) {
        logError(error);
        throw error;
    }
}
